import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Text } from "@stubhub/broadway-web";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import type { ComingUpForYouItem } from "src/http/types";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PAGE_IDS } from "src/shared/constants/PageIds";
import type { CarouselSize } from "src/types/shared/common";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { CarouselTitle } from "src/website-apps/shared/components/GenericCategoryCarousel.styled";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base/GridCardBase";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";

interface Props {
  readonly items: readonly ComingUpForYouItem[];
  readonly carouselSize: CarouselSize | undefined;
}

// Not every "Sports" category is a team matchup (tennis, golf, F1, etc. lack home/away teams), so
// eventMetadata may be absent even when isSportsMatchup is true.
const getMatchupEvent = (item: ComingUpForYouItem) => {
  if (!item.isSportsMatchup || !item.eventMetadata) return undefined;
  return item.eventId === undefined || item.eventId === null
    ? { eventMetadata: item.eventMetadata }
    : { eventId: item.eventId, eventMetadata: item.eventMetadata };
};

/**
 * Home-page "Coming up for you" carousel (experiment 103527 / DMS-225). Surfaces performers and
 * teams the user has previously purchased tickets for that have upcoming events near them, most
 * recently attended first. Sports categories render a matchup card (event name, dates, venue,
 * purchase-history anchor); non-sports keep the standard performer card format.
 *
 * Data arrives via props from the carousel registry (use-carousel-components.tsx): the SSR model
 * for control of exp 103068, or the lazy GetComingUpForYouItems fetch for variant. The registry's
 * `canParticipate` gate on the 103527 split means this component is only mounted when items exist.
 */
export const ComingUpForYouOnHome = ({ items, carouselSize }: Props) => {
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const carouselDim = useCarouselDimensions(carouselSize);
  const isBaseCardRefactor =
    useSimpleFeatureTernary(FeatureSetEnum.UseGridCardBaseComponentRefactor, false, true) ?? false;

  if (items.length === 0) return null;

  const renderItem = (item: ComingUpForYouItem, _index: number) => (
    <GridCardBase
      title={(item.isSportsMatchup ? item.eventName : item.name) ?? ""}
      imageUrl={item.imageUrl ?? ""}
      url={item.url ?? ""}
      entityId={item.categoryId}
      entityType={ENTITY_TYPES.CATEGORY}
      size={isMobile ? "mobile" : "default"}
      event={getMatchupEvent(item)}
      // MLB matchup images never showed in this carousel before; route MLB through the same
      // useShouldShowVerticalMatchupCard gate as non-MLB instead of the always-on fallback —
      // exp 103527 is still active here, so an ungated change would confound its own read.
      disableUngatedMlbMatchup
      formattedLocalEventsDates={
        (item.isSportsMatchup ? item.formattedEventDates : item.formattedLocalEventsDates) ?? undefined
      }
      {...(item.isSportsMatchup && item.venueName ? { venueName: item.venueName } : {})}
      {...(!item.isSportsMatchup && typeof item.availableLocalEvents === "number"
        ? { availableLocalEvents: item.availableLocalEvents }
        : {})}
    >
      {item.isSportsMatchup && !!item.purchaseAnchorText && (
        <Text variant="label" size={isMobile ? "extra-small" : "small"} color="text.hover" truncate>
          {item.purchaseAnchorText}
        </Text>
      )}
    </GridCardBase>
  );

  return (
    <div>
      <CarouselTitle $carouselLocation={PAGE_IDS.HOME} as="div">
        <Content id="cid$#4356655#" /> {/* "Coming up for you" */}
      </CarouselTitle>
      <ResponsiveCarousel
        carouselId={HOME_CAROUSEL_IDS.comingUpForYou}
        carouselKey="HomeComingUpForYou"
        items={items}
        isLoading={false}
        size={carouselDim.carouselSize}
        hidePaginationButtons={isMobile}
        imageHeightHint={carouselDim.imageSize.height}
        hasHoverEffect={!isMobile}
        analyticsCarouselId={CarouselId.ComingUpForYou}
        analyticsItemType={CarouselItemType.Category}
        renderItem={renderItem}
        useFlex={isBaseCardRefactor}
      />
    </div>
  );
};
